(() => {
  if (customElements.get('sunpura-energy-control')) return;
  class SunpuraEnergyControl extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.init());
    }
    init() {
      if (!this.isConnected) return;
      this.cleanup();
      this.selector = this.querySelector('[data-energy-selector]');
      this.tabs = Array.from(this.querySelectorAll('[data-energy-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-energy-panel]'));
      if (!this.selector || !this.tabs.length || this.tabs.length !== this.panels.length) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.selector.setAttribute('role', 'tablist');
      this.tabs.forEach((tab, index) => {
        tab.setAttribute('role', 'tab');
        const panel = this.panels[index];
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        tab.addEventListener('click', () => this.select(index), options);
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index + this.tabs.length - 1) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.select(next);
          this.tabs[next].focus();
        }, options);
      });
      this.addEventListener('shopify:block:select', event => {
        const index = this.panels.findIndex(panel => panel.contains(event.target) || panel.dataset.energyPanel === event.detail?.blockId);
        if (index >= 0) this.select(index);
      }, options);
      this.reduced.addEventListener('change', () => {
        if (this.reduced.matches) {
          this.panels.forEach(panel => panel.getAnimations().forEach(animation => animation.cancel()));
          this.stage.removeAttribute('data-resizing');
        }
      }, options);
      this.stage = this.querySelector('.sp-energy__panels');
      this.setAttribute('data-enhanced', '');
      this.selector.hidden = false;
      this.select(0, false);
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => { this.sizeStage(false); this.moveIndicator(); });
        this.panels.forEach(panel => this.observer.observe(panel));
        this.observer.observe(this.selector);
      } else {
        window.addEventListener('resize', () => this.sizeStage(false), options);
        this.querySelectorAll('img').forEach(img => img.addEventListener('load', () => this.sizeStage(false), options));
      }
    }
    sizeStage(animate) {
      const panel = this.panels?.[this.activeIndex];
      if (!panel || !this.stage) return;
      const height = Math.ceil(Math.max(...this.panels.map(item => item.getBoundingClientRect().height)));
      if (!height || height === this.stageHeight) return;
      this.stage.toggleAttribute('data-resizing', Boolean(animate && !this.reduced.matches));
      this.stage.style.setProperty('--sp-energy-stage-height', height + 'px');
      this.stageHeight = height;
    }
    select(index, animate = true) {
      if (!this.tabs[index]) return;
      this.activeIndex = index;
      this.moveIndicator();
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        const panel = this.panels[i];
        panel.setAttribute('aria-hidden', String(!active));
        panel.inert = !active;
        panel.tabIndex = active ? 0 : -1;
        panel.getAnimations().forEach(animation => animation.cancel());
      });
      this.sizeStage(animate && this.classList.contains('sp-energy--motion'));
      if (animate && this.classList.contains('sp-energy--motion') && !this.reduced.matches) {
        this.panels[index].animate([{ opacity: 0.35 }, { opacity: 1 }], { duration: 300, easing: 'cubic-bezier(.16,1,.3,1)' });
      }
    }
    moveIndicator() {
      const tab = this.tabs?.[this.activeIndex ?? this.active ?? 0];
      if (!tab || !this.selector) return;
      this.selector.style.setProperty('--sp-tab-x', tab.offsetLeft + 'px');
      this.selector.style.setProperty('--sp-tab-width', tab.offsetWidth + 'px');
    }
    cleanup() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.stageHeight = undefined;
      this.querySelectorAll('[data-energy-panel]').forEach(panel => {
        panel.getAnimations().forEach(animation => animation.cancel());
        panel.removeAttribute('aria-hidden');
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
        panel.removeAttribute('tabindex');
        panel.inert = false;
      });
      this.querySelector('.sp-energy__panels')?.style.removeProperty('--sp-energy-stage-height');
      this.removeAttribute('data-enhanced');
      const selector = this.querySelector('[data-energy-selector]');
      if (selector) selector.hidden = true;
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.cleanup();
    }
  }
  customElements.define('sunpura-energy-control', SunpuraEnergyControl);
})();
